import axios from 'axios';


const API_URL = process.env.NODE_ENV === 'production'
    ? '/api'  // Use relative paths in production (Apache will proxy)
    : 'http://localhost:5002/api';  // Use correct port in development

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const login = (loginData) => api.post('/auth/login', loginData);
export const register = (email, password) => api.post('/auth/register', { email, password });
export const refreshAccessToken = () => api.post('/auth/refresh');

export const getNotificationPreferences = async () => {
  const response = await api.get('/auth/notification-preferences');
  return response.data;
};

export const updateNotificationPreferences = async (preferences) => {
  const response = await api.put('/auth/notification-preferences', preferences);
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.put('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  });
  return response.data;
};

export const getAccounts = (includeArchived = false) => api.get('/accounts', { 
  params: includeArchived ? { include_archived: true } : {} 
});
export const createAccount = (name) => api.post('/accounts', { name });
export const getAccount = (id) => api.get(`/accounts/${id}`);
export const updateAccount = (id, name, is_active) => api.put(`/accounts/${id}`, { name, is_active });
export const deleteAccount = (id) => api.delete(`/accounts/${id}`);

// Account archival endpoints
export const archiveAccount = (id, reason = 'Account migration') => api.put(`/accounts/${id}/archive`, { reason });
export const reactivateAccount = (id) => api.put(`/accounts/${id}/reactivate`);
export const getArchivedAccounts = () => api.get('/accounts/archived');

export const getCategories = (includeDeleted = false) => api.get('/categories', {
  params: includeDeleted ? { include_deleted: true } : {}
});
export const createCategory = (name, account_id) => api.post('/categories', { name, account_id });
export const getCategory = (id) => api.get(`/categories/${id}`);
export const updateCategory = (id, name) => api.put(`/categories/${id}`, { name });
export const deleteCategory = (id) => api.delete(`/categories/${id}`);
export const getArchivedCategories = (accountId) => api.get(`/categories/archived/${accountId}`);
export const reactivateCategory = (categoryId) => api.put(`/categories/${categoryId}/reactivate`);
export const transferCategoryBalance = (sourceCategoryId, allocations, deprecateCategory = false) => api.post('/categories/transfer', {
  source_category_id: sourceCategoryId,
  allocations: allocations,
  deprecate_category: deprecateCategory
});

export const getTransactions = (accountId, dateFilter, categoryId) => api.get('/transactions', { params: { account_id: accountId, date_filter: dateFilter, category_id: categoryId } });
export const createTransaction = (data) => api.post('/transactions', data);
export const createIntake = (data, file) => {
  if (!file) return api.post('/transaction-intake', data);
  const body = new FormData();
  body.append('data', JSON.stringify(data));
  body.append('receipt', file);
  return api.post('/transaction-intake', body, { headers: { 'Content-Type': undefined } });
};
export const getIntakes = (params) => api.get('/transaction-intake', { params });
export const getIntakeSummary = () => api.get('/transaction-intake/summary');
export const getIntakeByRequest = (key) => api.get(`/transaction-intake/by-request/${key}`);
export const updateIntake = (id, data) => api.patch(`/transaction-intake/${id}`, data);
export const cancelIntake = (id, version) => api.post(`/transaction-intake/${id}/cancel`, { version });
export const retryIntake = (id, version) => api.post(`/transaction-intake/${id}/retry`, { version });
export const getIntakeReceipt = (id) => api.get(`/transaction-intake/${id}/receipt-url`);
export const getIntakeOriginal = (id, signal) => api.get(`/transaction-intake/${id}/original`, { responseType: 'arraybuffer', signal });
export const previewTally = (data) => api.post('/transaction-intake/preview', data);
export const settleTally = (data) => api.post('/transaction-intake/settle', data);
export const getTallyActivities = () => api.get('/transaction-intake/activities');
export const saveTallyActivity = (data) => api.post('/transaction-intake/activities', data);
export const deleteTallyActivity = (id) => api.delete(`/transaction-intake/activities/${id}`);
export const getTransaction = (id) => api.get(`/transactions/${id}`);
export const updateTransaction = (id, data) => api.put(`/transactions/${id}`, data);
export const previewTransactionDescriptions = (data) => api.post('/transactions/bulk-description/preview', data);
export const updateTransactionDescriptions = (data) => api.post('/transactions/bulk-description', data);
export const deleteTransaction = (id) => api.delete(`/transactions/${id}`);
export const previewTransactionBulk = (data) => api.post('/transactions/bulk/preview', data);
export const getPaycheckSubmission = (id) => api.get(`/transactions/paychecks/${encodeURIComponent(id)}`);
export const previewPaycheckCorrection = (data) => api.post('/transactions/paychecks/correction/preview', data);
export const applyPaycheckCorrection = (data) => api.post('/transactions/paychecks/correction/apply', data);
export const getSplitEvent = (id) => api.get(`/transactions/split-events/by-transaction/${id}`);
export const previewSplitCorrection = (data) => api.post('/transactions/split-events/correction/preview', data);
export const applySplitCorrection = (data) => api.post('/transactions/split-events/correction/apply', data);
export const previewSplitDeletion = (data) => api.post('/transactions/split-events/delete/preview', data);
export const applySplitDeletion = (data) => api.post('/transactions/split-events/delete/apply', data);
export const applyTransactionBulk = (data) => api.post('/transactions/bulk/apply', data);
export const exportSelectedTransactions = (ids) => api.post('/transactions/bulk/export', { transaction_ids: ids }, { responseType: 'blob' });
export const createTransfer = (data) => api.post('/transactions/transfer', data);

export const getTransactionDateRange = () => api.get('/transactions/date-range');

export const getCategoryTotals = (accountId) => api.get(`/accounts/${accountId}/category_totals`);

export const createPurchaseBatch = (data) => api.post('/purchase-batches', data);
export const getPurchaseBatches = () => api.get('/purchase-batches');
export const getPurchaseBatch = (id) => api.get(`/purchase-batches/${id}`);
export const undoPurchaseBatch = (id) => api.delete(`/purchase-batches/${id}`);
export const getPurchaseCategoryBalances = () => api.get('/purchase-batches/category-balances');

export const createSplitTemplate = (data) => api.post('/split_templates', data);
export const getSplitTemplates = () => api.get('/split_templates');
export const getSplitTemplate = (id) => api.get(`/split_templates/${id}`);
export const updateSplitTemplate = (id, data) => api.put(`/split_templates/${id}`, data);
export const deleteSplitTemplate = (id) => api.delete(`/split_templates/${id}`);

export const getAccountsSummary = () => api.get('/accounts/summary');

export const getAutopayments = () => api.get('/autopayments');
export const createAutopayment = (data) => api.post('/autopayments', data);
export const updateAutopayment = (id, data) => api.put(`/autopayments/${id}`, data);
export const deleteAutopayment = (id) => api.delete(`/autopayments/${id}`);
export const toggleAutopayment = (id) => api.post(`/autopayments/${id}/toggle`);

export const getAccountProjections = () => api.get('/accounts/projections');

// Analytics API endpoints
export const getAnalyticsOverview = () => api.get('/analytics/overview');

export const getAccountAnalytics = (accountId) => api.get(`/analytics/account_performance/${accountId}`);
export const getCategoryInsights = (accountId) => api.get(`/analytics/category_insights/${accountId}`);

// Migration API endpoints
export const validateSimpleTransfer = (sourceAccountId, destinationAccountId) => api.post('/migrations/validate-transfer', {
  source_account_id: sourceAccountId,
  destination_account_id: destinationAccountId
});

export const executeSimpleTransfer = (sourceAccountId, destinationAccountId, archiveReason = 'Account migration - simple transfer') => api.post('/migrations/transfer', {
  source_account_id: sourceAccountId,
  destination_account_id: destinationAccountId,
  archive_reason: archiveReason
});

// New two-mode transfer endpoints
export const executeSimpleTransferWithAutoCategory = (sourceAccountId, destinationAccountId) => api.post('/migrations/transfer-simple', {
  source_account_id: sourceAccountId,
  destination_account_id: destinationAccountId
});

export const executeDetailedTransfer = (sourceAccountId, destinationAccountId, allocations) => api.post('/migrations/transfer-detailed', {
  source_account_id: sourceAccountId,
  destination_account_id: destinationAccountId,
  allocations: allocations
});

export const validateConsolidation = (sourceAccountIds, newAccountName) => api.post('/migrations/validate-consolidation', {
  source_account_ids: sourceAccountIds,
  new_account_name: newAccountName
});

export const executeConsolidation = (sourceAccountIds, newAccountName, newCategories, archiveReason = 'Account migration - consolidation') => api.post('/migrations/consolidate', {
  source_account_ids: sourceAccountIds,
  new_account_name: newAccountName,
  new_categories: newCategories,
  archive_reason: archiveReason
});

// ============== TAX HELPER API ==============

// Tax Profile
export const getTaxProfile = () => api.get('/tax-helper/profile');
export const updateTaxProfile = (data) => api.put('/tax-helper/profile', data);

// Income Items
export const getTaxIncomeItems = (includeRetired = true) => api.get('/tax-helper/income-items', {
  params: { include_retired: includeRetired }
});
export const createTaxIncomeItem = (data) => api.post('/tax-helper/income-items', data);
export const updateTaxIncomeItem = (id, data) => api.put(`/tax-helper/income-items/${id}`, data);
export const retireTaxIncomeItem = (id, retiredDate = null) => api.put(`/tax-helper/income-items/${id}/retire`, {
  retired_date: retiredDate
});
export const deleteTaxIncomeItem = (id) => api.delete(`/tax-helper/income-items/${id}`);

// Allocation Links (Set-aside categories)
export const getTaxAllocationLinks = () => api.get('/tax-helper/allocation-links');
export const createTaxAllocationLink = (accountId, categoryId) => api.post('/tax-helper/allocation-links', {
  account_id: accountId,
  category_id: categoryId
});
export const deleteTaxAllocationLink = (id) => api.delete(`/tax-helper/allocation-links/${id}`);

// Quarterly Payments
export const getTaxPayments = (year = new Date().getFullYear()) => api.get('/tax-helper/payments', {
  params: { year }
});
export const confirmTaxPayment = (data) => api.post('/tax-helper/payments', data);
export const updateTaxPayment = (id, data) => api.put(`/tax-helper/payments/${id}`, data);
export const deleteTaxPayment = (id) => api.delete(`/tax-helper/payments/${id}`);

// Calculations
export const calculateTax = (year = new Date().getFullYear(), period = 'weekly') => api.get('/tax-helper/calculate', {
  params: { year, period }
});
export const getTaxProgress = (year = new Date().getFullYear()) => api.get('/tax-helper/progress', {
  params: { year }
});
export const getC2CActuals = (year = new Date().getFullYear()) => api.get('/tax-helper/c2c-actuals', {
  params: { year }
});
export const getTaxRecommendations = (year = new Date().getFullYear(), period = 'weekly') => api.get('/tax-helper/recommendations', {
  params: { year, period }
});

// Tax Constants (read-only)
export const getTaxConstants = (year = new Date().getFullYear()) => api.get('/tax-helper/constants', {
  params: { year }
});

// ============== TAX ADVISOR SNAPSHOT API ==============
export const getLatestTaxSnapshot = (year = new Date().getFullYear()) => api.get('/tax-advisor/snapshot/latest', {
  params: { year }
});
export const getTaxAdvisorReport = (year = new Date().getFullYear(), refresh = false) => api.get('/tax-advisor/advisor/report', {
  params: { year, refresh }
});

// ============== AFFORDABILITY EXPLORER API ==============

export const calculateAffordabilityScenario = (data) => api.post('/affordability/calculate', data);
export const exportAffordabilityScenario = (data) => api.post('/affordability/export', data);
export const calculateAffordabilityBaseline = (data) => api.post('/affordability/baseline', data);
export const getAffordabilityBaselineFromTaxHelper = () => api.get('/affordability/baseline-from-tax-helper');
export const getAffordabilityCategories = (data) => {
  if (data) {
    return api.post('/affordability/categories', data);
  }
  return api.get('/affordability/categories');
};
export const getAffordabilitySupportedStates = () => api.get('/affordability/supported-states');

// ============== AI CHAT API ==============

/**
 * Send a message to the AI chat service
 * @param {Object} data - Chat request data
 * @param {string} data.message - User's message
 * @param {string} [data.session_id] - Existing session ID (optional)
 * @param {Object} data.context - Page context object
 * @param {string} data.context.page - Current page identifier
 * @param {string} data.context.purpose - Page purpose description
 * @param {Object} [data.context.schema_fields] - Form field definitions
 * @param {Object} [data.context.current_values] - Current form values
 * @param {Object} [data.context.computed_results] - Computed analysis results
 * @param {boolean} data.context.is_complete - Whether all required fields are filled
 * @param {string[]} [data.context.missing_required] - List of missing required fields
 * @param {Object} [data.context.hints] - Additional context hints
 * @param {Array} [data.conversation_history] - Previous messages for context
 * @returns {Promise} - Response with AI message and metadata
 */
export const sendAIChatMessage = (data) => api.post('/ai/chat', data);

/**
 * Get user's chat history with optional filters
 * @param {Object} [params] - Filter parameters
 * @param {string} [params.page] - Filter by page name
 * @param {string} [params.start_date] - ISO date string
 * @param {string} [params.end_date] - ISO date string
 * @param {number} [params.limit] - Max results (default 50)
 * @returns {Promise} - Response with sessions list
 */
export const getAIChatHistory = (params = {}) => api.get('/ai/chat/history', { params });

/**
 * Get full details of a specific chat session
 * @param {string} sessionId - Session UUID
 * @returns {Promise} - Response with session details and messages
 */
export const getAIChatSession = (sessionId) => api.get(`/ai/chat/session/${sessionId}`);

/**
 * End a chat session explicitly
 * @param {string} sessionId - Session UUID
 * @returns {Promise} - Response with success status
 */
export const endAIChatSession = (sessionId) => api.post(`/ai/chat/session/${sessionId}/end`);

// ============== TELLER SYNC API ==============

export const triggerTellerSync = () => api.post('/teller/sync');
export const getTellerStatus = () => api.get('/teller/status');
export const getTellerAccounts = () => api.get('/teller/accounts');

// ============== COMPLIANCE ENGINE API ==============

export const getComplianceMonths = () => api.get('/compliance/months');
export const getComplianceMonth = (month) => api.get(`/compliance/month/${month}`);
export const reviewComplianceTransaction = (id, data) => api.post(`/compliance/transaction/${id}/review`, data);
export const uploadComplianceReceipt = (id, formData) => api.post(`/compliance/transaction/${id}/receipt`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const generateCompliancePurpose = (id) => api.post(`/compliance/transaction/${id}/ai-purpose`);
export const uploadChaseStatement = (month, formData) => api.post(`/compliance/month/${month}/statement`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const generateCompliancePacket = (month) => api.post(`/compliance/month/${month}/generate`);
export const signCompliancePacket = (month) => api.post(`/compliance/month/${month}/sign`);
export const emailCompliancePacket = (month, email, signed = true) => api.post(`/compliance/month/${month}/email-packet`, { email, signed });
export const getComplianceVendors = () => api.get('/compliance/vendors');
export const createComplianceVendor = (data) => api.post('/compliance/vendors', data);
export const updateComplianceVendor = (id, data) => api.put(`/compliance/vendor/${id}`, data);
export const getComplianceDashboardStatus = () => api.get('/compliance/dashboard-status');
export const aiEditCompliance = (data) => api.post('/compliance/ai-edit', data);
export const deleteComplianceTransaction = (id) => api.delete(`/compliance/transaction/${id}`);
export const generateDeductionSummary = (month) => api.post(`/compliance/month/${month}/deduction-summary`);
export const emailDeductionSummary = (month, email, format = 'xlsx') => api.post(`/compliance/month/${month}/email-summary`, { email, format });
export const requestDownloadCode = (email) => api.post('/compliance/download/request-code', { email });
export const verifyDownloadCode = (code) => api.post('/compliance/download/verify-code', { code });
export const getDownloadAuthStatus = () => api.get('/compliance/download/status');
export const getAllowedDownloadEmails = () => api.get('/compliance/download/allowed-emails');

export default api;

// Allocation Review is separate from existing Analytics.
export const getAllocationReview = params => api.get("/allocation-review", { params });
export const requestAllocationExplanation = (id, data) => api.post(`/allocation-review/categories/${id}/explanations`, data);
export const getAllocationExplanation = id => api.get(`/allocation-review/explanations/${id}`);
export const getAllocationTransactions = params => api.get("/allocation-review/transactions", { params });
export const calculateAllocationFunding = data => api.post('/allocation-review/calculator', data);
export const saveAllocationReviewSettings = (id, data) => api.put(`/allocation-review/categories/${id}/settings`, data);
export const saveAllocationExpenseContext = (categoryId, transactionId, data) => api.put(`/allocation-review/categories/${categoryId}/transactions/${transactionId}/expectation`, data);
export const setAllocationIncomeCadence = (id, cadence) => api.put(`/allocation-review/income-sources/${id}`, { cadence });
